import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import { ProgramsResponse } from '../../../shared/models/program.models';
import { ToastService } from '../../../core/services/toast.service';
import { InvestigationService } from '../../../core/services/investigation.service';
import { ProgramService } from '../../../core/services/program.service';
import { CategoryService } from '../../../core/services/category.service';
import { IncidentService } from '../../../core/services/incident.service';
import { SelectComponent, SelectOption } from '../select/select';
import {
  MultiselectDropdownComponent,
  MultiselectOption,
} from '../multiselect-dropdown/multiselect-dropdown';
import { DatePickerComponent } from '../date-picker/date-picker';
import { PersonSelectComponent } from '../person-select/person-select';
import { apiErrorMessage } from '../../utils/api-error';

type RequiredField = 'incident' | 'noticeDate' | 'person' | 'program' | 'categories';

@Component({
  selector: 'app-new-investigation-modal',
  imports: [
    SelectComponent,
    MultiselectDropdownComponent,
    DatePickerComponent,
    PersonSelectComponent,
  ],
  templateUrl: './new-investigation-modal.html',
})
export class NewInvestigationModalComponent implements OnInit {
  linkedIncidentId = input<string | null>(null);
  linkedIncidentLabel = input<string | null>(null);

  closed = output<void>();
  created = output<void>();

  private toast = inject(ToastService);
  private investigationService = inject(InvestigationService);
  private programService = inject(ProgramService);
  private categoryService = inject(CategoryService);
  private incidentService = inject(IncidentService);
  private destroyRef = inject(DestroyRef);

  protected isLinked = computed(() => !!this.linkedIncidentId());

  // ── Form state ─────────────────────────────────────────────────────────────
  protected investigationNoticeDate = signal('');
  protected personSendingNoticeId = signal('');
  protected investigatingProgramId = signal('');
  protected investigationCategorizations = signal<string[]>([]);
  protected selectedIncidentId = signal('');

  // ── Validation ─────────────────────────────────────────────────────────────
  // Per the spec, program, notice date, person sending the notice, at least one category and a
  // linked incident are all required at creation. Errors show only after the first submit.
  protected submitAttempted = signal(false);
  protected missing = computed(
    (): Record<RequiredField, boolean> => ({
      incident: !this.isLinked() && !this.selectedIncidentId(),
      noticeDate: !this.investigationNoticeDate(),
      person: !this.personSendingNoticeId(),
      program: !this.investigatingProgramId(),
      categories: this.investigationCategorizations().length === 0,
    }),
  );
  protected isValid = computed(() => !Object.values(this.missing()).some(Boolean));

  protected showError(field: RequiredField): boolean {
    return this.submitAttempted() && this.missing()[field];
  }

  // ── Options ────────────────────────────────────────────────────────────────
  protected programOptions = signal<SelectOption[]>([]);
  protected categoryOptions = signal<MultiselectOption[]>([]);
  protected incidentOptions = signal<SelectOption[]>([]);

  protected saving = signal(false);
  protected programsHasMore = signal(false);
  protected programsLoading = signal(false);
  private programTypeIdForII = signal<string | undefined>(undefined);
  private programPageIndex = 0;
  private readonly PROGRAM_PAGE_SIZE = 20;

  ngOnInit(): void {
    this.programService
      .getProgramTypes()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((res) => {
          const iiType = res.programTypes.find((t) => t.code === 'II');
          this.programTypeIdForII.set(iiType?.id);
          return this.loadProgramPage(0);
        }),
      )
      .subscribe({
        next: (res) => {
          this.programOptions.set(res.programs.map((p) => ({ value: p.id, label: p.name })));
          this.programsHasMore.set(res.programs.length < res.total);
          this.programPageIndex = 0;
          this.programsLoading.set(false);
        },
      });

    this.categoryService
      .getSituationCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) =>
          this.categoryOptions.set(
            res.situationCategories.map((c) => ({ id: c.id, label: c.name })),
          ),
      });

    if (!this.isLinked()) this.loadIncidentPage(0);
  }

  // GET /api/incidents always paginates and its text filter matches person names, not incident
  // numbers, so the picker pages through the list ("load more") and filters locally.
  private readonly INCIDENT_PAGE_SIZE = 50;
  private incidentPageIndex = 0;
  private incidentTotal = signal(0);
  protected incidentsLoading = signal(false);
  protected incidentsHasMore = computed(() => this.incidentOptions().length < this.incidentTotal());

  protected onLoadMoreIncidents(): void {
    if (this.incidentsLoading() || !this.incidentsHasMore()) return;
    this.loadIncidentPage(this.incidentPageIndex + 1);
  }

  private loadIncidentPage(pageIndex: number): void {
    this.incidentsLoading.set(true);
    this.incidentService
      .getIncidents({ pageSize: this.INCIDENT_PAGE_SIZE, pageIndex })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const page = res.incidents.map((i) => ({
            value: i.id,
            label: i.incidentNumber ? `${i.incidentNumber} — ${i.programName}` : i.id,
          }));
          this.incidentOptions.update((prev) => (pageIndex === 0 ? page : [...prev, ...page]));
          this.incidentPageIndex = pageIndex;
          this.incidentTotal.set(res.total);
          this.incidentsLoading.set(false);
        },
        error: () => this.incidentsLoading.set(false),
      });
  }

  private loadProgramPage(pageIndex: number): Observable<ProgramsResponse> {
    this.programsLoading.set(true);
    return this.programService.getPrograms(
      { pageSize: this.PROGRAM_PAGE_SIZE, pageIndex },
      this.programTypeIdForII(),
    );
  }

  protected onLoadMorePrograms(): void {
    if (this.programsLoading()) return;
    const nextPage = this.programPageIndex + 1;
    this.loadProgramPage(nextPage)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.programOptions.update((prev) => [
            ...prev,
            ...res.programs.map((p) => ({ value: p.id, label: p.name })),
          ]);
          this.programsHasMore.set(this.programOptions().length < res.total);
          this.programPageIndex = nextPage;
          this.programsLoading.set(false);
        },
      });
  }

  protected onSubmit(): void {
    if (this.saving()) return;
    this.submitAttempted.set(true);
    if (!this.isValid()) return;

    const incidentId = this.isLinked() ? this.linkedIncidentId()! : this.selectedIncidentId();

    this.saving.set(true);
    this.investigationService
      .createInvestigation({
        investigationNoticeDate: this.investigationNoticeDate(),
        personSendingNoticeId: this.personSendingNoticeId(),
        investigatingProgramId: this.investigatingProgramId(),
        investigationCategorizations: this.investigationCategorizations(),
        incidentInvestigations: [incidentId],
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.toast.show('Investigation created successfully');
          this.created.emit();
          this.closed.emit();
        },
        error: (err) => {
          this.saving.set(false);
          this.toast.show(apiErrorMessage(err, 'Failed to create investigation'), 'error');
        },
      });
  }
}
